import { baseApi } from '../../services/baseApi';
import { ApiEndPoints } from '../../constants/apiEndPoints';

/**
 * An admin official (Administrative Level Directory). Exact field names aren't
 * guaranteed, so values are read through the accessors below.
 */
export interface AdminOfficial {
  id: number;
  full_name?: string | null;
  email?: string | null;
  contact?: string | null;
  designation?: string | null;
  department?: string | null;
  administrative_level?: string | null;
  organization?: number | null;
  created_at?: string | null;
}

/** Request body for POST/PATCH /admin-official/. */
export interface AdminOfficialPayload {
  full_name: string;
  email: string;
  contact: string;
  designation: string;
  department: string;
  administrative_level: string;
  organization: number;
}

export interface AdminOfficialImportResult {
  created?: number;
  updated?: number;
  skipped?: number;
  errors?: string[];
}

/** Distinct filter values for the directory's Department / Administrative Level dropdowns. */
export interface AdminOfficialFilters {
  departments: string[];
  administrative_levels: string[];
}

/* ------------------------------------------------------------------ *
 * Defensive accessors.
 * ------------------------------------------------------------------ */
export const officialName = (o: AdminOfficial) => o.full_name || '';
export const officialEmail = (o: AdminOfficial) => o.email || '';
export const officialPhone = (o: AdminOfficial) => o.contact || '';
export const officialDesignation = (o: AdminOfficial) => o.designation || '';
export const officialDepartment = (o: AdminOfficial) => o.department || '';
export const officialLevel = (o: AdminOfficial) => o.administrative_level || '';

/** DRF list responses may be a plain array or a paginated `{ results }` envelope. */
type Listish<T> = T[] | { count?: number; results?: T[] };
const asList = <T>(r: Listish<T>): T[] => (Array.isArray(r) ? r : (r?.results ?? []));

/** Normalized list result: current page rows plus the server's total count. */
export interface AdminOfficialsResponse {
  items: AdminOfficial[];
  total: number;
  page: number;
  pageSize: number;
}

export const adminOfficialsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getAdminOfficials: build.query<
      AdminOfficialsResponse,
      {
        search?: string;
        department?: string;
        administrativeLevel?: string;
        page?: number;
        pageSize?: number;
      } | void
    >({
      query: (arg) => ({
        url: `${ApiEndPoints.adminOfficial}/`,
        params: {
          ...(arg?.search ? { search: arg.search } : {}),
          ...(arg?.department ? { department: arg.department } : {}),
          ...(arg?.administrativeLevel ? { administrative_level: arg.administrativeLevel } : {}),
          page: arg?.page ?? 1,
          page_size: arg?.pageSize ?? 10,
        },
      }),
      transformResponse: (r: Listish<AdminOfficial>, _meta, arg) => {
        const items = asList(r);
        const total = Array.isArray(r) ? r.length : (r?.count ?? items.length);
        return { items, total, page: arg?.page ?? 1, pageSize: arg?.pageSize ?? 10 };
      },
      providesTags: ['AdminOfficials'],
    }),
    getAdminOfficial: build.query<AdminOfficial, number | string>({
      query: (id) => ({ url: `${ApiEndPoints.adminOfficial}/${id}/` }),
      providesTags: ['AdminOfficials'],
    }),
    // Distinct department / administrative-level values for the directory filters.
    getAdminOfficialFilters: build.query<AdminOfficialFilters, void>({
      query: () => ({ url: `${ApiEndPoints.adminOfficialFilters}/` }),
      transformResponse: (r?: Partial<AdminOfficialFilters>) => ({
        departments: r?.departments ?? [],
        administrative_levels: r?.administrative_levels ?? [],
      }),
      providesTags: ['AdminOfficials'],
    }),
    createAdminOfficial: build.mutation<AdminOfficial, AdminOfficialPayload>({
      query: (body) => ({ url: `${ApiEndPoints.adminOfficial}/`, method: 'POST', body }),
      invalidatesTags: ['AdminOfficials'],
    }),
    updateAdminOfficial: build.mutation<
      AdminOfficial,
      { id: number | string; patch: Partial<AdminOfficialPayload> }
    >({
      query: ({ id, patch }) => ({
        url: `${ApiEndPoints.adminOfficial}/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: ['AdminOfficials'],
    }),
    deleteAdminOfficial: build.mutation<void, number | string>({
      query: (id) => ({ url: `${ApiEndPoints.adminOfficial}/${id}/`, method: 'DELETE' }),
      invalidatesTags: ['AdminOfficials'],
    }),
    // Download the server-generated bulk-upload template (.xlsx).
    getAdminOfficialTemplate: build.query<Blob, void>({
      query: () => ({
        url: `${ApiEndPoints.adminOfficialImportTemplate}/`,
        responseHandler: (response) => response.blob(),
        cache: 'no-cache',
      }),
    }),
    // Bulk-upload a filled template (multipart/form-data).
    importAdminOfficials: build.mutation<AdminOfficialImportResult, { file: File }>({
      query: ({ file }) => {
        const form = new FormData();
        form.append('file', file);
        return { url: `${ApiEndPoints.adminOfficialImport}/`, method: 'POST', body: form };
      },
      invalidatesTags: ['AdminOfficials'],
    }),
  }),
});

export const {
  useGetAdminOfficialsQuery,
  useGetAdminOfficialQuery,
  useGetAdminOfficialFiltersQuery,
  useCreateAdminOfficialMutation,
  useUpdateAdminOfficialMutation,
  useDeleteAdminOfficialMutation,
  useLazyGetAdminOfficialTemplateQuery,
  useImportAdminOfficialsMutation,
} = adminOfficialsApi;
